import { useEffect, useRef } from "react";
import { useAppDispatch } from "../../../app/hooks";
import { RefurbishedPendingOrderInterface } from "../../../common/interfaces/apiV2/refurbished/RefurbishedPendingOrderInterface";
import { setCartOpen } from "../CartSlice";
import { CartTimerResult } from "../utils/cartTimer";

interface UseExpiredCartCleanupParams {
    pendingOrder: RefurbishedPendingOrderInterface | null;
    timer: CartTimerResult;
    removeReservedProduct: (productId: number) => {
        unwrap: () => Promise<unknown>;
    };
    refetchPendingOrder: () => unknown;
    refetchProducts: () => unknown;
}

export const useExpiredCartCleanup = ({
    pendingOrder,
    timer,
    removeReservedProduct,
    refetchPendingOrder,
    refetchProducts,
}: UseExpiredCartCleanupParams): void => {
    const dispatch = useAppDispatch();
    const isClearingExpiredRef = useRef(false);

    useEffect(() => {
        if (!pendingOrder?.expires_at || timer.remainingMs > 0) {
            return;
        }

        const orderLines = pendingOrder.order_lines;

        if (!orderLines.length) {
            void refetchPendingOrder();
            void refetchProducts();
            return;
        }

        if (isClearingExpiredRef.current) {
            return;
        }

        isClearingExpiredRef.current = true;

        void (async () => {
            try {
                await Promise.all(
                    orderLines.map((line) =>
                        removeReservedProduct(
                            line.refurbished_product_id,
                        ).unwrap(),
                    ),
                );
                dispatch(setCartOpen(false));
            } catch {
                // Partial failures are reconciled via refetch.
            } finally {
                isClearingExpiredRef.current = false;
                await refetchPendingOrder();
                await refetchProducts();
            }
        })();
    }, [
        pendingOrder?.expires_at,
        pendingOrder?.order_lines,
        timer.remainingMs,
        removeReservedProduct,
        refetchPendingOrder,
        refetchProducts,
        dispatch,
    ]);
};
